import { Injectable } from '@angular/core';
import { LanguageEntry } from '../shared/constants/language-levels';
import { BeraterUebersicht } from '../shared/model/beraterUebersicht.model';
import { FilterOptions, TravelWillingness } from '../shared/model/filter.model';
import { Katalog } from '../shared/model/katalog.model';
import { IFaehigkeit } from '../shared/model/faehigkeit.model';
import { Requests } from '../shared/model/request.model';
import { LanguageOption } from '../shared/constants/languages';
import {
  PROFILER_LANGUAGES,
  ProfilerLanguage,
  toProfilerLanguage,
} from '../shared/constants/profiler-language';
import { Moment } from 'moment';
import { Advisor } from '../shared/model/advisor.model';
import { ProfileCompleteness } from '../shared/model/profile-completeness';

const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}$/;

/**
 * NP20-1192: Zugriff auf die einmal je Datensatz gerechneten Ampelwerte. Der Sorter
 * rechnet sie bewusst nicht selbst — die Uebersicht merkt sie sich, damit `renderUsers()`
 * bei jedem Tastendruck nicht das ganze Regelwerk erneut ueber alle Berater laufen laesst.
 */
export type CompletenessLookup = (
  consultant: BeraterUebersicht,
) => ProfileCompleteness | null | undefined;

@Injectable({ providedIn: 'root' })
export class SorterService {
  applyFilter(
    consultant: BeraterUebersicht[],
    filter: FilterOptions,
    sorting?: string,
    completeness?: CompletenessLookup,
  ) {
    let consultantCopy = [...consultant];

    consultantCopy = this.getMatchingAvailability(
      consultantCopy,
      filter.availableFrom,
    );

    consultantCopy = this.getMatchingBranches(
      consultantCopy,
      filter.branches,
      filter.isBranchesAndMode,
    );

    consultantCopy = this.getMatchingCatalog(
      consultantCopy,
      filter.catalog,
      filter.isCatalogAndMode,
    );

    consultantCopy = this.getMatchingConsultantName(
      consultantCopy,
      filter.consultantName,
    );

    consultantCopy = this.getMatchingApplicant(
      consultantCopy,
      filter.isApplicant,
    );

    consultantCopy = this.getMatchingFreelancer(
      consultantCopy,
      filter.isFreelancer,
    );

    consultantCopy = this.getMatchingLanguages(
      consultantCopy,
      filter.languages,
      filter.isLanguagesAndMode,
    );

    consultantCopy = this.getMatchingProfilerLanguage(
      consultantCopy,
      filter.profilerLanguage,
    );

    consultantCopy = this.getMatchingEducation(
      consultantCopy,
      filter.education,
      filter.isEducationAndMode,
    );

    consultantCopy = this.getMatchingEducationInstitution(
      consultantCopy,
      filter.educationInstitution,
      filter.isEducationInstitutionAndMode,
    );

    consultantCopy = this.getMatchingProjectCompany(
      consultantCopy,
      filter.projectCompany,
      filter.isProjectCompanyAndMode,
    );

    consultantCopy = this.getMatchingFocuses(
      consultantCopy,
      filter.focuses,
      filter.isFocusesAndMode,
    );

    consultantCopy = this.getMatchingProjectRole(
      consultantCopy,
      filter.projectRole,
      filter.isProjectRolesAndMode,
    );

    consultantCopy = this.getMatchingTravel(
      consultantCopy,
      filter.travelWillingness,
    );

    consultantCopy = this.getMatchingSkill(
      consultantCopy,
      filter.skills,
      filter.isSkillsAndMode,
    );

    consultantCopy = this.getMatchingTrainings(
      consultantCopy,
      filter.trainings,
      filter.isTrainingsAndMode,
    );

    consultantCopy = this.getMatchingCertificates(
      consultantCopy,
      filter.certificates,
      filter.isCertificatesAndMode,
    );

    consultantCopy = this.getMatchingAdvisor(consultantCopy, filter.advisor);

    consultantCopy = this.getMatchingSecurityCheck(
      consultantCopy,
      filter.securityCheckCompletedOn,
    );

    if (sorting) {
      consultantCopy = this.applySorting(consultantCopy, sorting, completeness);
    }

    return consultantCopy;
  }

  applySorting(
    consultantOverview: BeraterUebersicht[],
    sorting: string,
    completeness?: CompletenessLookup,
  ) {
    switch (sorting) {
      case 'availableDesc':
        return consultantOverview.sort(
          (a, b) =>
            new Date(b.verfuegbar as Date).getTime() -
            new Date(a.verfuegbar as Date).getTime(),
        );
      case 'availableAsc':
        return consultantOverview.sort(
          (a, b) =>
            new Date(a.verfuegbar as Date).getTime() -
            new Date(b.verfuegbar as Date).getTime(),
        );
      case 'nameDesc':
        return consultantOverview.sort((a, b) =>
          b.name.localeCompare(a.name, 'de'),
        );
      case 'nameAsc':
        return consultantOverview.sort((a, b) =>
          a.name.localeCompare(b.name, 'de'),
        );
      case 'consultantDesc':
        return consultantOverview.sort(
          (a, b) => parseInt(b.beraterSeit, 10) - parseInt(a.beraterSeit, 10),
        );
      case 'consultantAsc':
        return consultantOverview.sort(
          (a, b) => parseInt(a.beraterSeit, 10) - parseInt(b.beraterSeit, 10),
        );
      case 'profilerLanguage':
        return consultantOverview.sort((a, b) => {
          const byLanguage =
            this.profilerLanguageRank(a.langKey) -
            this.profilerLanguageRank(b.langKey);
          return byLanguage !== 0
            ? byLanguage
            : (a.name ?? '').localeCompare(b.name ?? '', 'de');
        });
      // NP20-1190: Badges absteigend, bei Gleichstand Prozent aufsteigend, dann Name A-Z.
      // Berater ohne Ampelwerte (unvollstaendige Antwort) stehen hinten, statt mit einer
      // geratenen Null nach oben zu rutschen.
      case 'openBadgesDesc':
        return consultantOverview.sort((a, b) => {
          const left = completeness?.(a) ?? null;
          const right = completeness?.(b) ?? null;
          const byBadges =
            (right?.totalBadges ?? -1) - (left?.totalBadges ?? -1);
          if (byBadges !== 0) return byBadges;
          const byPercent = (left?.percent ?? 101) - (right?.percent ?? 101);
          if (byPercent !== 0) return byPercent;
          return (a.name ?? '').localeCompare(b.name ?? '', 'de');
        });
      default:
        return consultantOverview;
    }
  }

  private getMatchingAdvisor(
    consultantOverview: BeraterUebersicht[],
    advisor: Advisor | null,
  ) {
    if (!advisor?.id) return consultantOverview;

    return consultantOverview.filter((c) => c?.superVisorId === advisor.id);
  }

  private getMatchingAvailability(
    consultantOverview: BeraterUebersicht[],
    availableFrom: Moment | null,
  ) {
    if (!availableFrom) return consultantOverview;
    return consultantOverview.filter((berater) => {
      const consultantDate = new Date(berater?.verfuegbar as Date);
      const availableDate = new Date(
        consultantDate.getFullYear(),
        consultantDate.getMonth(),
        consultantDate.getDate(),
      );
      let filterDate = availableFrom.toDate();
      filterDate = new Date(
        filterDate.getFullYear(),
        filterDate.getMonth(),
        filterDate.getDate(),
      );
      return availableDate <= filterDate;
    });
  }

  private getMatchingBranches(
    consultantOverview: BeraterUebersicht[],
    branches: Requests[],
    andMode: boolean,
  ) {
    if (branches.length === 0) return consultantOverview;

    const filterBranches = branches
      .map((b) => (b?.name ?? '').toLowerCase().trim())
      .filter(Boolean);

    if (filterBranches.length === 0) return consultantOverview;

    return consultantOverview.filter((c) => {
      if (!c.branchen || c.branchen.length === 0) return false;

      const advisorBranches = c.branchen
        .map((b) => (b ?? '').toLowerCase().trim())
        .filter(Boolean);

      return andMode
        ? filterBranches.every((f) =>
            advisorBranches.some((a) => a.includes(f)),
          )
        : advisorBranches.some((a) =>
            filterBranches.some((f) => a.includes(f)),
          );
    });
  }

  private getMatchingCatalog(
    consultantOverview: BeraterUebersicht[],
    catalog: Katalog[],
    andMode: boolean,
  ) {
    if (catalog.length === 0) return consultantOverview;
    const catalogIds = catalog.map((c) => c.id);

    return consultantOverview.filter((c) => {
      if (!c.catalogAssignments || c.catalogAssignments.length === 0)
        return false;
      const consultantCatIds = c.catalogAssignments.map((a) => a.catalogId);
      return andMode
        ? catalogIds.every((id) => consultantCatIds.includes(id))
        : consultantCatIds.some((id) => catalogIds.includes(id));
    });
  }

  private getMatchingSkill(
    consultantOverview: BeraterUebersicht[],
    skills: IFaehigkeit[],
    andMode: boolean,
  ) {
    if (skills.length === 0) return consultantOverview;
    const skillIds = skills.map((s) => s.id);

    return consultantOverview.filter((c) => {
      if (!c.projekte || c.projekte.length === 0) return false;
      const consultantSkillIds = c.projekte
        .flatMap((p) => p.tasks || [])
        .flatMap((t) => t.skills || [])
        .map((s) => s.id);
      return andMode
        ? skillIds.every((id) => consultantSkillIds.includes(id))
        : consultantSkillIds.some((id) => skillIds.includes(id));
    });
  }

  private getMatchingConsultantName(
    consultantOverview: BeraterUebersicht[],
    name: string,
  ) {
    if (!name) return consultantOverview;
    const search = name.toLowerCase().trim();
    return consultantOverview.filter((c) =>
      c.name.toLowerCase().trim().includes(search),
    );
  }

  private getMatchingApplicant(
    consultantOverview: BeraterUebersicht[],
    isApplicant: boolean,
  ) {
    if (!isApplicant) return consultantOverview;
    return consultantOverview.filter((c) => c.applicant === true);
  }

  private getMatchingFreelancer(
    consultantOverview: BeraterUebersicht[],
    isFreelancer: boolean,
  ) {
    if (!isFreelancer) return consultantOverview;
    return consultantOverview.filter((c) => c.freelancer === true);
  }

  private getMatchingEducation(
    consultantOverview: BeraterUebersicht[],
    education: string[],
    andMode: boolean,
  ) {
    if (!education || education.length === 0) return consultantOverview;
    return consultantOverview.filter((c) => {
      if (!c.ausbildung || c.ausbildung.length === 0) return false;
      const eduList = c.ausbildung.map((a) => a.ausbildungsgang.toLowerCase());
      return andMode
        ? education.every((e) =>
            eduList.some((a) => a.includes(e.toLowerCase())),
          )
        : eduList.some((a) =>
            education.some((e) => a.includes(e.toLowerCase())),
          );
    });
  }

  private getMatchingEducationInstitution(
    consultantOverview: BeraterUebersicht[],
    institutions: string[],
    andMode: boolean,
  ) {
    if (!institutions || institutions.length === 0) return consultantOverview;
    return consultantOverview.filter((c) => {
      if (!c.ausbildung || c.ausbildung.length === 0) return false;
      const instList = c.ausbildung.map((a) =>
        a.ausbildungseinrichtung.toLowerCase(),
      );
      return andMode
        ? institutions.every((i) =>
            instList.some((a) => a.includes(i.toLowerCase())),
          )
        : instList.some((a) =>
            institutions.some((i) => a.includes(i.toLowerCase())),
          );
    });
  }

  private getMatchingProjectCompany(
    consultantOverview: BeraterUebersicht[],
    projects: string[],
    andMode: boolean,
  ) {
    if (!projects || projects.length === 0) return consultantOverview;
    return consultantOverview.filter((c) => {
      if (!c.projekte || c.projekte.length === 0) return false;
      return andMode
        ? projects.every((p) =>
            c.projekte.some((proj) =>
              proj.kunde.toLowerCase().includes(p.toLowerCase()),
            ),
          )
        : c.projekte.some((proj) =>
            projects.some((p) =>
              proj.kunde.toLowerCase().includes(p.toLowerCase()),
            ),
          );
    });
  }

  private getMatchingFocuses(
    consultantOverview: BeraterUebersicht[],
    focuses: Requests[],
    andMode: boolean,
  ) {
    const filterFocuses = (focuses ?? [])
      .map((f) => (f.name ?? '').toLowerCase().trim())
      .filter(Boolean);

    if (filterFocuses.length === 0) return consultantOverview;

    return consultantOverview.filter((c) => {
      const advisor = (c.schwerpunkte ?? [])
        .map((s) => (s ?? '').toLowerCase().trim())
        .filter(Boolean);

      if (advisor.length === 0) return false;

      return andMode
        ? filterFocuses.every((f) => advisor.some((a) => a.includes(f)))
        : advisor.some((a) => filterFocuses.some((f) => a.includes(f)));
    });
  }

  private getMatchingProjectRole(
    consultantOverview: BeraterUebersicht[],
    roles: string[],
    andMode: boolean,
  ) {
    if (!roles || roles.length === 0) return consultantOverview;
    return consultantOverview.filter((c) => {
      if (!c.projekte || c.projekte.length === 0) return false;
      return andMode
        ? roles.every((r) =>
            c.projekte.some((proj) =>
              proj.stelle?.toLowerCase().includes(r.toLowerCase()),
            ),
          )
        : c.projekte.some((proj) =>
            roles.some((r) =>
              proj.stelle?.toLowerCase().includes(r.toLowerCase()),
            ),
          );
    });
  }

  private getMatchingTrainings(
    consultantOverview: BeraterUebersicht[],
    trainings: Requests[],
    andMode: boolean,
  ) {
    const filterTrainings = (trainings ?? [])
      .map((t) => (t?.name ?? '').toLowerCase().trim())
      .filter(Boolean);

    if (filterTrainings.length === 0) return consultantOverview;

    return consultantOverview.filter((c) => {
      const consultantTrainings = (c.schulungen ?? [])
        .map((t) => (t?.lehrgang ?? '').toLowerCase().trim())
        .filter(Boolean);

      if (consultantTrainings.length === 0) return false;

      return andMode
        ? filterTrainings.every((f) =>
            consultantTrainings.some((ct) => ct.includes(f)),
          )
        : consultantTrainings.some((ct) =>
            filterTrainings.some((f) => ct.includes(f)),
          );
    });
  }

  private getMatchingCertificates(
    consultantOverview: BeraterUebersicht[],
    certificates: string[],
    andMode: boolean,
  ) {
    const selectedCertificates = (certificates ?? [])
      .map((c) => (c ?? '').toLowerCase().trim())
      .filter(Boolean);

    if (selectedCertificates.length === 0) return consultantOverview;

    return consultantOverview.filter((c) => {
      const consultantCertificates = (c.zertifikate ?? [])
        .map((z) => (z?.lehrgang ?? '').toLowerCase().trim())
        .filter(Boolean);

      if (consultantCertificates.length === 0) return false;

      return andMode
        ? selectedCertificates.every((selectedCertificate) =>
            consultantCertificates.some((consultantCertificate) =>
              consultantCertificate.includes(selectedCertificate),
            ),
          )
        : consultantCertificates.some((consultantCertificate) =>
            selectedCertificates.some((selectedCertificate) =>
              consultantCertificate.includes(selectedCertificate),
            ),
          );
    });
  }

  private getMatchingSecurityCheck(
    consultantOverview: BeraterUebersicht[],
    completedOn: Moment | null,
  ) {
    if (!completedOn) return consultantOverview;
    const filterDate = completedOn.toDate();
    filterDate.setHours(0, 0, 0, 0);

    return consultantOverview.filter((c) => {
      if (!c.securityChecks || c.securityChecks.length === 0) return false;
      return c.securityChecks.some((sc) => {
        if (!sc.completedOn) return false;
        const completed = new Date(sc.completedOn);
        completed.setHours(0, 0, 0, 0);
        return completed <= filterDate;
      });
    });
  }

  private getMatchingTravel(
    consultantOverview: BeraterUebersicht[],
    travel: TravelWillingness,
  ) {
    if (!travel) return consultantOverview;
    return consultantOverview.filter((c) => c.reisebereitschaft === travel);
  }

  private readonly languageMap: Record<string, string> = {
    deutsch: 'de',
    german: 'de',
    englisch: 'en',
    english: 'en',
    arabisch: 'ar',
    arabic: 'ar',
    chinesisch: 'zh',
    chinese: 'zh',
    filipino: 'fil',
    tl: 'fil',
    tagalog: 'fil',
    französisch: 'fr',
    french: 'fr',
    griechisch: 'el',
    greek: 'el',
    hindi: 'hi',
    italienisch: 'it',
    italian: 'it',
    japanisch: 'ja',
    japanese: 'ja',
    koreanisch: 'ko',
    korean: 'ko',
    litauisch: 'lt',
    lithuanian: 'lt',
    niederländisch: 'nl',
    dutch: 'nl',
    persisch: 'fa',
    farsi: 'fa',
    persian: 'fa',
    polnisch: 'pl',
    polish: 'pl',
    portugiesisch: 'pt',
    portuguese: 'pt',
    russisch: 'ru',
    russian: 'ru',
    serbisch: 'sr',
    serbian: 'sr',
    spanisch: 'es',
    spanish: 'es',
    tamil: 'ta',
    türkisch: 'tr',
    turkish: 'tr',
    ukrainisch: 'uk',
    ukrainian: 'uk',
    ungarisch: 'hu',
    hungarian: 'hu',
    vietnamesisch: 'vi',
    vietnamese: 'vi',
  };

  private getMatchingLanguages(
    consultants: BeraterUebersicht[],
    languages: LanguageOption[],
    andMode: boolean,
  ): BeraterUebersicht[] {
    const selected = new Set(
      (languages ?? [])
        .map((l) => l?.code?.toLowerCase().trim())
        .filter(Boolean),
    );

    if (!selected.size) return consultants;

    return consultants.filter((c) => {
      const user = new Set(this.normalizeUserLanguagesToCodes(c?.sprachen));
      if (!user.size) return false;

      return [...selected][andMode ? 'every' : 'some']((code) =>
        user.has(code),
      );
    });
  }

  // Berater ohne Profiler-Sprache stehen hinter allen Sprachen.
  private profilerLanguageRank(langKey?: string): number {
    const language = toProfilerLanguage(langKey);
    return language
      ? PROFILER_LANGUAGES.indexOf(language)
      : PROFILER_LANGUAGES.length;
  }

  private getMatchingProfilerLanguage(
    consultants: BeraterUebersicht[],
    profilerLanguage: ProfilerLanguage | null,
  ): BeraterUebersicht[] {
    if (!profilerLanguage) return consultants;

    return consultants.filter(
      (c) => toProfilerLanguage(c?.langKey) === profilerLanguage,
    );
  }

  public normalizeUserLanguagesToCodes(value: LanguageEntry[] | string[]): string[] {
    if (!Array.isArray(value)) {
      return String(value ?? '').split(/[,;/]/)
        .map((v) => this.languageTokenToCode(v.trim()))
        .filter(Boolean);
    }
    return (value as Array<LanguageEntry | string>).map((v) => {
      if (typeof v === 'object' && v !== null && 'language' in v) {
        return this.languageTokenToCode((v as LanguageEntry).language.trim());
      }
      return this.languageTokenToCode(String(v).trim());
    }).filter(Boolean);
  }

  private languageTokenToCode(token: string): string {
    const normalized = (token ?? '').toLowerCase().trim();
    const alias = this.languageMap[normalized];
    if (alias) return alias;
    return LANGUAGE_CODE_PATTERN.test(normalized) ? normalized : '';
  }
}
